import * as React from 'react';
import { ActionLink } from '@cian/ui-kit';

import { ISavedFilter } from '../../../../store/saved_filters/actions';
import { SavedFiltersIds } from '../../../../interfaces/filters';

import * as styles from './FitInOneLine.css';

export interface IFitInOneLineProps {
  children?: React.ReactNode[];
  enabled: boolean;
  maxWidth: number;
  items: ISavedFilter[];
  onItemsClear(): void;
  toggle(value: boolean): void;
}

export interface IFitInOneLineState {
  needRecalcuclateItemsWidth: boolean;
  itemsWidths: number[];
}

const BUTTON_WIDTH = 80;
const CLEAR_BUTTON_WIDTH = 90;

export class FitInOneLine extends React.PureComponent<IFitInOneLineProps, IFitInOneLineState> {
  public state = {
    itemsWidths: [] as number[],
    needRecalcuclateItemsWidth: true,
  };

  private get maxWidth() {
    return this.props.maxWidth - CLEAR_BUTTON_WIDTH;
  }

  private wrapper = React.createRef<HTMLDivElement>();

  public componentDidMount() {
    this.updateItemsWidths();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IFitInOneLineProps) {
    if (this.props.children !== nextProps.children || this.props.maxWidth !== nextProps.maxWidth) {
      this.setState({
        needRecalcuclateItemsWidth: true,
      });
    }
  }

  public componentDidUpdate() {
    this.updateItemsWidths();
  }

  public render() {
    const { children, enabled, maxWidth, items } = this.props;

    if (!maxWidth) {
      return null;
    }

    const { itemsWidths, needRecalcuclateItemsWidth } = this.state;

    const childrensArray = React.Children.toArray(children);
    let lastVisibleItemIndex: number | undefined;

    const sumOfVisibleItemsWidths = itemsWidths.reduce((acc, item, index) => {
      const newAcc = acc + item;
      if (lastVisibleItemIndex) {
        return acc;
      }

      if (!lastVisibleItemIndex && newAcc > this.maxWidth) {
        lastVisibleItemIndex = index;
      }

      return newAcc;
    }, 0);
    const isButtonVisible = Boolean(lastVisibleItemIndex);

    const onlyRoadAndRoadDistanceTags =
      childrensArray.length === 2 &&
      items.filter(i => i.id === SavedFiltersIds.Road || i.id === SavedFiltersIds.RoadDistance).length === 2;
    const onlyStationAndStationDistanceTags =
      childrensArray.length === 2 &&
      items.filter(i => i.id === SavedFiltersIds.Station || i.id === SavedFiltersIds.StationDistance).length === 2;

    const isClearButtonVisible =
      childrensArray.length > 1 && !onlyRoadAndRoadDistanceTags && !onlyStationAndStationDistanceTags;

    // Если нужно показать кнопку "Показать ещё", проверяем влезает ли она
    if (isButtonVisible && this.maxWidth - sumOfVisibleItemsWidths < BUTTON_WIDTH) {
      lastVisibleItemIndex = Number(lastVisibleItemIndex) - 1;
    }

    if (!enabled) {
      return (
        <div className={styles['wrapper--inline']} ref={this.wrapper}>
          {children}
          {isButtonVisible && this.showEnableButton()}
          {isClearButtonVisible && this.renderClearButton()}
        </div>
      );
    }

    return (
      <div className={styles['wrapper']} ref={this.wrapper}>
        {childrensArray.map((item, index) => {
          if (needRecalcuclateItemsWidth) {
            return item;
          } else {
            if (lastVisibleItemIndex && index >= Number(lastVisibleItemIndex)) {
              return null;
            }

            return item;
          }
        })}
        {isButtonVisible && this.showDisableButton()}
        {isClearButtonVisible && this.renderClearButton()}
      </div>
    );
  }

  private renderClearButton = () => (
    <span className={styles['button']}>
      <ActionLink aria-label="Очистить" onClick={this.props.onItemsClear}>
        Очистить
      </ActionLink>
    </span>
  );

  private showEnableButton = () => {
    return (
      <span className={styles['button']}>
        <ActionLink aria-label="Свернуть" onClick={() => this.props.toggle(true)}>
          Свернуть
        </ActionLink>
      </span>
    );
  };

  private showDisableButton = () => (
    <span className={styles['button']}>
      <ActionLink onClick={() => this.props.toggle(false)}>•••</ActionLink>
    </span>
  );

  private updateItemsWidths = () => {
    if (!this.state.needRecalcuclateItemsWidth || !this.wrapper.current) {
      return;
    }

    const items = this.wrapper.current.childNodes;
    const itemsWidths = [];
    let i = 0;
    // Берём только первые 15 элементов, чтобы не пересчитывать лишнее
    // Предполагается, что в одну строку не помещается больше 15 тегов
    const lastIndex = Math.min(items.length, 15);
    for (i; i < lastIndex; i++) {
      const element = items[i] as HTMLDivElement;
      if (element && element.tagName.toLowerCase() !== 'button') {
        const computedStyle = window.getComputedStyle(element);
        itemsWidths.push(
          element.getBoundingClientRect().width + getValueFromComputedStyle(computedStyle, 'marginRight'),
        );
      }
    }

    this.setState({
      itemsWidths,
      needRecalcuclateItemsWidth: false,
    });
  };
}

function getValueFromComputedStyle(computedStyle: CSSStyleDeclaration, key: keyof CSSStyleDeclaration): number {
  return Math.round(parseFloat(computedStyle[key] as string) || 0);
}
